<template>
  <div
    ref="listboxEl"
    class="tm-interactive-list"
    :class="{ 'tm-interactive-list--overflow': overflow }"
  >
    <div v-bind="listboxAttrs">
      <div
        v-for="(opt, i) in internalItems"
        :id="opt.listUid"
        :ref="(el) => (itemsELs[i] = el as HTMLElement)"
        :key="opt.listUid"
        :class="{ 'tm-interactive-list--disabled': opt.disabled }"
        :aria-disabled="opt.disabled"
        :aria-selected="optionIndex === i && !opt.disabled"
        role="option"
        tabindex="-1"
        @mousemove="onMousemove(i)"
        @mouseout="onMouseout"
        @click="onClick(opt)"
      >
        <slot v-bind="{ opt: items[i], index: i, focused: optionIndex === i }" />
      </div>
    </div>
    <div ref="bottomElement"></div>
    <div
      v-if="isLoadingMore"
      class="flex flex-center my-1"
    >
      <tm-spinner color="blue" />
    </div>
  </div>
</template>

<script setup lang="ts" generic="TOption">
import { debounce, isObject } from 'lodash-es'
import type { PropType } from '@/composition/vue/compositionApi'
import {
  computed,
  getCurrentInstance,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from '@/composition/vue/compositionApi'
import { keyCodes } from '@/constants/keyCodes'
import { normalizeToInterval, stopAndPrevent } from '@/utils'
import TmSpinner from '@/components/shared/TmSpinner.vue'
import { getWindowService } from '@/core/container/ioc'

type InternalItem = TOption & {
  disabled: boolean
  listUid: string
}

const { keyEnter, keyDown, keyUp, keyPageDown, keyPageUp } = keyCodes

const props = defineProps({
  overflow: {
    type: Boolean,
  },
  initialIndex: {
    type: Number,
    default: -1,
  },
  autoHighlightFirstOption: {
    type: Boolean,
  },
  optionDisabled: {
    type: String,
    default: 'disabled',
  },
  items: {
    type: Array as PropType<TOption[]>,
    required: true,
  },
  loadMoreItemsHandler: {
    type: Function as PropType<() => void>,
  },
  isLoadingMore: {
    type: Boolean,
  },
  comboboxId: {
    type: Number,
  },
})

const emit = defineEmits<{
  'update:model-value': [_val: InternalItem]
  'on-select': []
}>()

const windowService = getWindowService()
const instanceUid = getCurrentInstance()?.uid
const optionIndex = ref<number>(props.initialIndex)
const listboxEl = ref<HTMLElement>()
const scrollbarWrapsEl = ref<HTMLElement | null>(null)
const itemsELs = ref<HTMLElement[]>([])
const bottomElement = ref<HTMLElement>()

const internalId = computed(() => props.comboboxId || instanceUid)
const internalItems = computed(() =>
  props.items.map<InternalItem>((el, i) => ({
    ...el,
    disabled: isOptionDisabled(el),
    listUid: `${internalId.value}_${i}`,
  })),
)
const optionsLength = computed(() => internalItems.value.length)
const itemNode = computed<HTMLElement>(() => itemsELs.value[optionIndex.value])
const optionIndexInRange = computed(() => optionIndex.value > -1 && optionIndex.value < optionsLength.value)
const listboxAttrs = computed(() => {
  const attrs = {
    id: `${internalId.value}_lb`,
    role: 'listbox',
    tabindex: '-1',
    'aria-activedescendant': '',
  }

  if (optionIndex.value >= 0) {
    attrs['aria-activedescendant'] = `${internalId.value}_${optionIndex.value}`
  }

  return attrs
})

const getPropValueFn = (option: TOption, property: string) => {
  if (isObject(option) && property in option) {
    return option[property]
  }
  return option
}

const isOptionDisabled = (option: TOption) => Boolean(getPropValueFn(option, props.optionDisabled) === true)

const getFirstSelectableIndex = () => internalItems.value.findIndex((item) => !item.disabled)

const getSelectableIndexes = () =>
  internalItems.value.reduce<number[]>((acc, item, index) => {
    if (!item.disabled) {
      acc.push(index)
    }
    return acc
  }, [])

const defaultOptionIndex = computed(() =>
  props.autoHighlightFirstOption ? getFirstSelectableIndex() : props.initialIndex,
)

const scrollTarget = (el: HTMLElement) => {
  const scrollbarWrap = props.overflow ? listboxEl.value : scrollbarWrapsEl.value
  if (!el || !scrollbarWrap) {
    if (scrollbarWrap) scrollbarWrap.scrollTop = 0
    return
  }

  requestAnimationFrame(() => {
    const { scrollTop, clientHeight: wrapHeight } = scrollbarWrap
    const wrapRect = scrollbarWrap.getBoundingClientRect()
    const elRect = el.getBoundingClientRect()

    const offsetTop = elRect.top - wrapRect.top + scrollTop
    const elHeight = el.offsetHeight
    const totalElHeight = offsetTop + elHeight

    if (totalElHeight > scrollTop + wrapHeight) {
      scrollbarWrap.scrollTop = totalElHeight - wrapHeight
    } else if (offsetTop < scrollTop) {
      scrollbarWrap.scrollTop = offsetTop
    }
  })
}

const isDisabledIndex = (index: number): boolean => {
  const disabled = isOptionDisabled(props.items[index])
  return index !== -1 && index !== optionIndex.value && disabled
}

let isKeyboardNavigating = false
const enableMouseEvents = debounce(() => {
  isKeyboardNavigating = false
}, 150)

const moveOptionSelection = (offset = 1) => {
  let index = optionIndex.value

  do {
    index = normalizeToInterval(index + offset, -1, optionsLength.value - 1)
  } while (isDisabledIndex(index))

  if (optionIndex.value !== index) {
    optionIndex.value = index
    isKeyboardNavigating = true
    enableMouseEvents()
    scrollTarget(itemNode.value)
  }
}

const navigateOptions = (direction: 1 | -1) => {
  const indexes = getSelectableIndexes()
  if (!indexes.length) {
    return
  }
  const currentPosition = indexes.indexOf(optionIndex.value)
  let nextPosition: number
  if (currentPosition === -1) {
    nextPosition = direction === 1 ? 0 : indexes.length - 1
  } else {
    nextPosition = (currentPosition + direction + indexes.length) % indexes.length
  }
  optionIndex.value = indexes[nextPosition]
  isKeyboardNavigating = true
  enableMouseEvents()
  scrollTarget(itemNode.value)
}

const highlightDefaultOption = () => {
  optionIndex.value = defaultOptionIndex.value
  nextTick(() => scrollTarget(itemNode.value))
}

const onClick = (option: InternalItem) => {
  emit('update:model-value', option)
  emit('on-select')
}

const onKeydown = (e: KeyboardEvent) => {
  const handledKeys = [keyEnter, keyDown, keyUp, keyPageDown, keyPageUp]
  if (!e.target || !handledKeys.includes(e.code)) {
    return
  }

  if (e.code === keyPageUp || e.code === keyPageDown) {
    stopAndPrevent(e)
    optionIndex.value = -1
    moveOptionSelection(e.code === keyPageUp ? 1 : -1)
  }

  // up, down
  if (e.code === keyUp || e.code === keyDown) {
    stopAndPrevent(e)
    if (props.autoHighlightFirstOption) {
      navigateOptions(e.code === keyUp ? -1 : 1)
    } else {
      moveOptionSelection(e.code === keyUp ? -1 : 1)
    }
  }

  // enter
  if (e.code === keyEnter && optionIndexInRange.value) {
    stopAndPrevent(e)
    const firstElementChild = itemNode.value.firstElementChild as HTMLElement
    firstElementChild.click()
  }
}

const onMousemove = (i: number) => {
  if (isKeyboardNavigating) return
  optionIndex.value = i
}

const onMouseout = () => {
  if (isKeyboardNavigating) return
  optionIndex.value = defaultOptionIndex.value
}

const scrollToInitial = () => {
  optionIndex.value = props.initialIndex
  scrollTarget(itemNode.value)
}

const initScroll = () => {
  const getScrollbarWraps = (el?: HTMLElement): HTMLElement | null => {
    const wrapper = el?.parentElement

    if (!wrapper) {
      return null
    }

    const style = windowService.getStyle(wrapper)
    const overflow = [style.overflow, style.overflowY]
    if (overflow.some((item) => !/scroll|auto/.test(item))) {
      return getScrollbarWraps(wrapper)
    }

    return wrapper
  }

  if (!props.overflow) {
    scrollbarWrapsEl.value = getScrollbarWraps(listboxEl.value)
  }
  if (props.initialIndex !== -1) {
    nextTick(() => scrollTarget(itemNode.value))
  }
}

let observer: IntersectionObserver | null = null
const initObserver = () => {
  const intersectHandler = props.loadMoreItemsHandler
  if (!intersectHandler) {
    return
  }
  observer = new IntersectionObserver(
    (entries) => {
      const entry = entries[0]
      if (!entry.isIntersecting) {
        return
      }
      intersectHandler()
    },
    {
      root: listboxEl.value,
      rootMargin: '0px 0px 100px 0px',
    },
  )
  observer.observe(bottomElement.value!)
}

onMounted(() => {
  initScroll()
  initObserver()
  if (props.autoHighlightFirstOption) {
    highlightDefaultOption()
  }
})

watch(
  () => props.items,
  (newItems, oldItems) => {
    if (!props.autoHighlightFirstOption) {
      return
    }
    if (oldItems && newItems.length > oldItems.length) {
      return
    }
    highlightDefaultOption()
  },
)

onBeforeUnmount(() => {
  observer?.disconnect()
  enableMouseEvents.cancel()
})

defineExpose({
  onKeydown,
  scrollToInitial,
})
</script>

<style lang="scss" scoped>
@import '@/styles/mixins.scss';

.tm-interactive-list {
  &--overflow {
    @include thumb-with-track;
    overflow-y: auto;
    overflow-x: hidden;
  }
  &--disabled,
  &--disabled :deep(*) {
    cursor: default;
  }
}
</style>
